<script lang="ts" context="module">
    import {
        type CalendarDate,
        CalendarDateTime,
        type DateValue,
        toZoned,
        type ZonedDateTime,
    } from '@internationalized/date';

    export const zonedNullable = {
        placeholderType: 'CalendarDateTime' as const,
        areEqual: (a: ZonedDateTime | null, b: ZonedDateTime | null) => {
            if (a == null && b == null) {
                return true;
            } else if (a != null && b != null) {
                return a.toAbsoluteString() === b.toAbsoluteString();
            } else {
                return false;
            }
        },
        toDateValue: (v: ZonedDateTime | null) =>
            v == null ? null : toCalendarDateTime(v),
        toFieldValue: (d: DateValue | null) =>
            d == null ? null : toZoned(d, getLocalTimeZone()),
    } as const;

    export const dateTimeNullable = {
        placeholderType: 'CalendarDateTime' as const,
        areEqual: (a: CalendarDateTime | null, b: CalendarDateTime | null) => {
            if (a == null && b == null) {
                return true;
            } else if (a != null && b != null) {
                return a.toString() === b.toString();
            } else {
                return false;
            }
        },
        toDateValue: (v: CalendarDateTime | null) =>
            v == null ? null : toCalendarDateTime(v),
        toFieldValue: (d: DateValue | null) => (d == null ? null : toCalendarDateTime(d)),
    } as const;

    export const dateNullable = {
        placeholderType: 'CalendarDate' as const,
        areEqual: (a: CalendarDate | null, b: CalendarDate | null) => {
            if (a == null && b == null) {
                return true;
            } else if (a != null && b != null) {
                return a.toString() === b.toString();
            } else {
                return false;
            }
        },
        toDateValue: (v: CalendarDate | null) => (v == null ? null : toCalendarDate(v)),
        toFieldValue: (d: DateValue | null) => (d == null ? null : toCalendarDate(d)),
    } as const;
</script>

<script lang="ts" generics="TValues, TValue">
    import { createDateField, createDatePicker, createSync, melt } from '@melt-ui/svelte';
    import { fly } from 'svelte/transition';
    import {
        getLocalTimeZone,
        now,
        toCalendarDate,
        toCalendarDateTime,
    } from '@internationalized/date';
    import type { Field, Form } from '@jmvdevelop/svelte-form';
    import { getContainer } from '$components/Context/ContainerContext';
    import Calendar from '$components/Date/Calendar.svelte';
    import { match } from 'ts-pattern';
    import { quadOut } from 'svelte/easing';

    export let form: Form<TValues, string>;
    export let field: Field<TValues, TValue>;

    export let placeholderType: 'CalendarDate' | 'CalendarDateTime' | 'ZonedDateTime' =
        'CalendarDateTime';

    export let toDateValue: (v: TValue) => DateValue | null;
    export let toFieldValue: (date: DateValue | null) => TValue;
    export let areEqual: (a: TValue, b: TValue) => boolean;
    export let minValue: DateValue | undefined = undefined;
    export let maxValue: DateValue | undefined = undefined;
    export let futureDateOnly = false;

    const { intl } = getContainer();

    let fieldValue = form.stores().fieldValue(field);

    const {
        elements: {
            calendar,
            cell,
            content,
            field: fieldEl,
            grid,
            heading,
            nextButton,
            prevButton,
            segment,
            trigger,
        },
        states: {
            months,
            headingValue,
            weekdays,
            segmentContents,
            open,
            placeholder,
            value,
        },
        helpers: { isDateDisabled, isDateUnavailable },
        options: { locale },
    } = createDatePicker({
        forceVisible: true,
        locale: intl().getLocale(),
        defaultValue: toDateValue($fieldValue) ?? undefined,
        defaultPlaceholder: createPlaceholder(placeholderType),
        minValue,
        maxValue,
        isDateDisabled: (date: DateValue): boolean => {
            if (futureDateOnly) {
                let currentDate = toCalendarDate(now(getLocalTimeZone()));
                let startOfDay = toCalendarDate(date);

                return currentDate.compare(startOfDay) > 0;
            } else {
                return false;
            }
        },
    });

    const {
        elements: { field: timeField, segment: timeSegment },
        states: {
            placeholder: timePlaceholder,
            segmentContents: timeSegmentContents,
            value: timeValue,
        },
        ids: { hour: timeHourId },
    } = createDateField({
        locale: intl().getLocale(),
        defaultValue: toDateValue($fieldValue) ?? undefined,
        defaultPlaceholder: createPlaceholder(placeholderType),
        minValue,
        maxValue,
    });

    $: $placeholder = createPlaceholder(placeholderType);
    $: $timePlaceholder = createPlaceholder(placeholderType);

    $: hourSegmentIndex = $timeSegmentContents.findIndex(
        (segment) => segment.part === 'hour',
    );

    const sync = createSync({
        value,
        timeValue,
    });

    $: sync.timeValue($value, (v) => {
        $value = v;
    });

    $: sync.value(toDateValue($fieldValue) ?? undefined, (v) => {
        const oldFieldValue = field.getValue(form.getState().values);
        const nextFieldValue = toFieldValue(v ?? null);

        if (!areEqual(oldFieldValue, nextFieldValue)) {
            form.dispatch(form.actions().changeValue(nextFieldValue, field));
        }
    });

    function createPlaceholder(type: typeof placeholderType) {
        const n = now(getLocalTimeZone());
        return match(type)
            .with('CalendarDate', () => toCalendarDate(n))
            .with('CalendarDateTime', () => toCalendarDateTime(n))
            .with('ZonedDateTime', () => n)
            .exhaustive();
    }

    function onCellClick() {
        const timeInput = document.querySelector(`#${$timeHourId}`) as HTMLElement | null;
        timeInput?.focus();
    }
</script>

<section class="root">
    <div use:melt={$fieldEl} class="control-form control-form-date">
        {#key $locale}
            {#each $segmentContents as seg}
                <div use:melt={$segment(seg.part)} class="segment">
                    {seg.value}
                </div>
            {/each}
        {/key}
        <div class="wrap-btn-calendar">
            <button use:melt={$trigger} type="button" class="btn-leo btn-calendar">
                <i class="fa fa-calendar" aria-hidden={true} aria-label="Calendar" />
            </button>
        </div>
    </div>

    {#if $open}
        <div
            transition:fly={{ duration: 150, easing: quadOut, y: '-10px', opacity: 0 }}
            use:melt={$content}
            class="calendar-popover"
        >
            <Calendar
                {calendar}
                {cell}
                {grid}
                {heading}
                {nextButton}
                {prevButton}
                {months}
                {headingValue}
                {weekdays}
                {isDateDisabled}
                {isDateUnavailable}
                {onCellClick}
            />
            {#if hourSegmentIndex !== -1}
                <div use:melt={$timeField} class="control-form control-form-time">
                    {#key $locale}
                        {#each $timeSegmentContents as seg, index}
                            {#if index >= hourSegmentIndex}
                                <div use:melt={$timeSegment(seg.part)} class="segment">
                                    {seg.value}
                                </div>
                            {/if}
                        {/each}
                    {/key}
                </div>
            {/if}
        </div>
    {/if}
</section>

<style lang="postcss">
    .root {
        @apply flex w-full @container;
    }

    .control-form-date {
        @apply py-0 px-2 @[220px]:px-5;
        @apply flex items-center;

        .segment {
            @apply py-5 px-1 @[250px]:px-2;
            @apply data-[segment=literal]:px-0;
        }
    }

    .wrap-btn-calendar {
        @apply flex w-full items-center justify-end;
        @apply ml-2 @[200px]:ml-4;
    }

    .btn-calendar {
        @apply btn-leo-gray-blue rounded-[8px];
    }

    .calendar-popover {
        @apply z-10 min-w-[320px] rounded-lg bg-white shadow-leo;
    }

    .control-form-time {
        @apply my-4 py-0 px-5 flex items-center justify-center mx-auto max-w-1/3;

        .segment {
            @apply py-5 px-2;
            @apply data-[segment=literal]:px-0;
        }
    }
</style>
